// On-screen piano keyboard (2 octaves). Emits noteOn/noteOff via callbacks for
// mouse/touch (single-pointer glissando). Owns the displayed base octave and can
// light keys from external triggers (computer keyboard / MIDI in Phase 11).

const WHITE = [0, 2, 4, 5, 7, 9, 11];
const isBlack = (semi: number): boolean => !WHITE.includes(((semi % 12) + 12) % 12);

export interface KeyboardOptions {
  octaves?: number;
  octave?: number; // base octave (4 => C4 = MIDI 60)
  onNoteOn: (midi: number) => void;
  onNoteOff: (midi: number) => void;
  onOctaveChange?: (octave: number) => void;
}

export class Keyboard {
  private readonly root: HTMLElement;
  private readonly opts: Required<Omit<KeyboardOptions, 'onOctaveChange'>> &
    Pick<KeyboardOptions, 'onOctaveChange'>;
  private octave: number;
  private pressing = false;
  private pointerMidi: number | null = null;
  private keyEls = new Map<number, HTMLElement>();

  constructor(root: HTMLElement, options: KeyboardOptions) {
    this.root = root;
    this.opts = { octaves: 2, octave: 4, ...options };
    this.octave = this.opts.octave;
    this.build();
    this.attach();
  }

  get baseMidi(): number {
    return (this.octave + 1) * 12; // octave 4 -> 60
  }

  getOctave(): number {
    return this.octave;
  }

  setOctave(oct: number): void {
    const clamped = Math.max(0, Math.min(8, oct));
    if (clamped === this.octave) return;
    this.octave = clamped;
    this.build();
    this.opts.onOctaveChange?.(this.octave);
  }

  shiftOctave(delta: number): void {
    this.setOctave(this.octave + delta);
  }

  /** Light/unlight a key (for computer-keyboard / MIDI feedback). */
  setKeyVisual(midi: number, down: boolean): void {
    const el = this.keyEls.get(midi);
    if (el) el.dataset.down = String(down);
  }

  private build(): void {
    this.root.replaceChildren();
    this.keyEls.clear();
    const base = this.baseMidi;
    const count = this.opts.octaves * 12 + 1;

    // First pass: count whites for positioning.
    let totalWhite = 0;
    for (let i = 0; i < count; i++) if (!isBlack(base + i)) totalWhite++;
    const whiteW = 100 / totalWhite;

    let whiteIdx = 0;
    for (let i = 0; i < count; i++) {
      const midi = base + i;
      if (isBlack(midi)) {
        const key = document.createElement('div');
        key.className = 'key-black';
        key.style.left = `${whiteIdx * whiteW}%`;
        key.style.width = `${whiteW * 0.6}%`;
        key.dataset.midi = String(midi);
        this.root.appendChild(key);
        this.keyEls.set(midi, key);
      } else {
        const key = document.createElement('div');
        key.className = 'key-white';
        key.dataset.midi = String(midi);
        this.root.appendChild(key);
        this.keyEls.set(midi, key);
        whiteIdx++;
      }
    }
  }

  private midiFromPoint(x: number, y: number): number | null {
    const el = document.elementFromPoint(x, y) as HTMLElement | null;
    const midi = el?.dataset?.midi;
    return midi ? Number(midi) : null;
  }

  private press(midi: number): void {
    if (this.pointerMidi === midi) return;
    if (this.pointerMidi !== null) this.releaseCurrent();
    this.pointerMidi = midi;
    this.setKeyVisual(midi, true);
    this.opts.onNoteOn(midi);
  }

  private releaseCurrent(): void {
    if (this.pointerMidi === null) return;
    this.setKeyVisual(this.pointerMidi, false);
    this.opts.onNoteOff(this.pointerMidi);
    this.pointerMidi = null;
  }

  private attach(): void {
    this.root.addEventListener('pointerdown', (e) => {
      this.pressing = true;
      this.root.setPointerCapture(e.pointerId);
      const midi = this.midiFromPoint(e.clientX, e.clientY);
      if (midi !== null) this.press(midi);
      e.preventDefault();
    });
    this.root.addEventListener('pointermove', (e) => {
      if (!this.pressing) return;
      const midi = this.midiFromPoint(e.clientX, e.clientY);
      if (midi !== null) this.press(midi);
    });
    const end = (e: PointerEvent): void => {
      this.pressing = false;
      this.releaseCurrent();
      try {
        this.root.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
    };
    this.root.addEventListener('pointerup', end);
    this.root.addEventListener('pointercancel', end);
  }
}
